import {
  signInWithCustomToken,
  signOut,
  onAuthStateChanged,
  onIdTokenChanged,
  type User as FirebaseAuthUser,
  type Auth,
} from 'firebase/auth';
import { auth } from './config';
import { getLogger } from '../logger';

/**
 * FirebaseAuthService handles Firebase Authentication with automatic token refresh.
 *
 * Best Practices:
 * - Firebase SDK automatically refreshes tokens when getIdToken() is called if expired
 * - onIdTokenChanged listener is the recommended way to detect token refreshes
 * - We use onIdTokenChanged to proactively refresh tokens before expiration
 * - Page visibility API prevents unnecessary refreshes when tab is hidden
 */
export class FirebaseAuthService {
  private auth: Auth;
  private idTokenUnsubscribe: (() => void) | null = null;
  private proactiveRefreshTimeout: ReturnType<typeof setTimeout> | null = null;
  private visibilityUnsubscribe: (() => void) | null = null;
  // Firebase tokens expire after 1 hour, refresh proactively at 90% of lifetime (54 minutes)
  private readonly TOKEN_LIFETIME_MS = 60 * 60 * 1000; // 1 hour
  private readonly PROACTIVE_REFRESH_RATIO = 0.9; // Refresh at 90% of token lifetime

  constructor() {
    this.auth = auth;
    this.setupPageVisibilityHandling();
  }

  /**
   * Sign in with a custom token and start automatic token refresh
   */
  async signInWithCustomToken(customToken: string): Promise<FirebaseAuthUser> {
    const userCredential = await signInWithCustomToken(this.auth, customToken);
    // Start automatic token refresh after sign in
    this.startTokenRefresh();
    return userCredential.user;
  }

  /**
   * Sign out and stop token refresh
   */
  async signOut(): Promise<void> {
    this.stopTokenRefresh();
    await signOut(this.auth);
  }

  /**
   * Get the current user's ID token.
   * Firebase SDK automatically refreshes expired tokens when this is called.
   *
   * @param forceRefresh - If true, forces a token refresh even if current token is valid
   */
  async getIdToken(forceRefresh: boolean = false): Promise<string | null> {
    const user = this.auth.currentUser;
    if (!user) {
      return null;
    }

    return await user.getIdToken(forceRefresh);
  }

  /**
   * Listen to authentication state changes.
   * Automatically starts/stops token refresh based on auth state.
   */
  onAuthStateChanged(
    callback: (user: FirebaseAuthUser | null) => void
  ): () => void {
    return onAuthStateChanged(this.auth, (user) => {
      if (user) {
        // Start token refresh when user is authenticated
        this.startTokenRefresh();
      } else {
        // Stop token refresh when user signs out
        this.stopTokenRefresh();
      }
      callback(user);
    });
  }

  /**
   * Listen to ID token changes (fires when token is refreshed).
   * This is the recommended Firebase way to detect token refreshes.
   *
   * @param callback - Called whenever the ID token changes
   * @returns Unsubscribe function
   */
  onIdTokenChanged(
    callback: (user: FirebaseAuthUser | null) => void
  ): () => void {
    return onIdTokenChanged(this.auth, (user) => {
      callback(user);
    });
  }

  /**
   * Start automatic token refresh mechanism using onIdTokenChanged.
   * This is the Firebase-recommended approach for token refresh.
   *
   * The Firebase SDK automatically refreshes tokens when:
   * 1. getIdToken() is called and token is expired/about to expire
   * 2. onIdTokenChanged fires when token is refreshed
   *
   * We proactively refresh tokens before expiration to avoid any edge cases.
   */
  private startTokenRefresh(): void {
    // Clear any existing refresh mechanism
    this.stopTokenRefresh();

    const user = this.auth.currentUser;
    if (!user) {
      return;
    }

    // Set up listener for token changes (Firebase SDK's primary refresh mechanism)
    this.idTokenUnsubscribe = onIdTokenChanged(
      this.auth,
      async (currentUser) => {
        if (currentUser) {
          // Token was refreshed by Firebase SDK, ensure it's cached
          try {
            await currentUser.getIdToken(false);
            // Schedule next proactive refresh
            this.scheduleProactiveRefresh();
          } catch (error) {
            const logger = getLogger();
            logger.error('Error getting refreshed token', {
              error: error instanceof Error ? error.message : String(error),
            });
            this.stopTokenRefresh();
          }
        } else {
          // User signed out
          this.stopTokenRefresh();
        }
      }
    );

    // Schedule initial proactive refresh
    this.scheduleProactiveRefresh();
  }

  /**
   * Schedule a proactive token refresh before expiration.
   * This ensures tokens are refreshed before they expire, avoiding edge cases.
   */
  private scheduleProactiveRefresh(): void {
    // Clear any existing timeout
    if (this.proactiveRefreshTimeout) {
      clearTimeout(this.proactiveRefreshTimeout);
      this.proactiveRefreshTimeout = null;
    }

    // Calculate when to refresh (90% of token lifetime)
    const refreshDelay = this.TOKEN_LIFETIME_MS * this.PROACTIVE_REFRESH_RATIO;

    this.proactiveRefreshTimeout = setTimeout(async () => {
      const currentUser = this.auth.currentUser;
      // Only refresh if user is still authenticated and page is visible
      if (currentUser && !document.hidden) {
        try {
          // Force refresh to get a new token before expiration
          await currentUser.getIdToken(true);
        } catch (error) {
          const logger = getLogger();
          logger.error('Error proactively refreshing token', {
            error: error instanceof Error ? error.message : String(error),
          });
          // If refresh fails, stop the refresh mechanism
          this.stopTokenRefresh();
        }
      } else if (currentUser) {
        // Page is hidden, reschedule for when it becomes visible
        this.scheduleProactiveRefresh();
      }
    }, refreshDelay);
  }

  /**
   * Stop automatic token refresh and clean up resources
   */
  private stopTokenRefresh(): void {
    if (this.proactiveRefreshTimeout) {
      clearTimeout(this.proactiveRefreshTimeout);
      this.proactiveRefreshTimeout = null;
    }

    if (this.idTokenUnsubscribe) {
      this.idTokenUnsubscribe();
      this.idTokenUnsubscribe = null;
    }
  }

  /**
   * Set up page visibility handling to avoid unnecessary refreshes when tab is hidden.
   * When page becomes visible again, we refresh the token if needed.
   */
  private setupPageVisibilityHandling(): void {
    if (typeof document === 'undefined') {
      return; // SSR environment
    }

    const handleVisibilityChange = () => {
      if (!document.hidden && this.auth.currentUser) {
        // Page became visible, check if token needs refresh
        // Firebase SDK will handle this automatically on next getIdToken() call
        // But we can proactively refresh if token is close to expiration
        const user = this.auth.currentUser;
        user
          .getIdToken()
          .then(() => {
            // Reschedule proactive refresh
            this.scheduleProactiveRefresh();
          })
          .catch((error) => {
            const logger = getLogger();
            logger.error('Error refreshing token on visibility change', {
              error: error instanceof Error ? error.message : String(error),
            });
          });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    this.visibilityUnsubscribe = () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }

  /**
   * Clean up all listeners and timeouts
   */
  destroy(): void {
    this.stopTokenRefresh();
    if (this.visibilityUnsubscribe) {
      this.visibilityUnsubscribe();
      this.visibilityUnsubscribe = null;
    }
  }

  isAuthenticated(): boolean {
    return this.auth.currentUser !== null;
  }
}

export const firebaseAuthService = new FirebaseAuthService();
